import type { EnvVar } from '@/api/schemas';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { useNow } from '@/lib/useNow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { EnvVarsCards } from './EnvVarsCards';
import { EnvVarsDesktopTable } from './EnvVarsDesktopTable';
import { CardsSkeleton, TableSkeleton } from './EnvVarsSkeletons';
import { envListState } from './envHelpers';

export interface EnvVarActions {
  onEdit: (v: EnvVar) => void;
  onNewVersion: (v: EnvVar) => void;
  onReveal: (v: EnvVar) => void;
  onVersions: (v: EnvVar) => void;
  onDelete: (v: EnvVar) => void;
}

interface Props extends EnvVarActions {
  vars: EnvVar[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  onCreate: () => void;
  scopeReady: boolean;
}

/** Screen 06 list: key, visibility, type, version, updated, fingerprint, warnings; cards on phones. */
export function EnvVarsTable(props: Props) {
  const bp = useBreakpoint();
  const now = useNow(30_000);
  const { vars, isLoading, isError, error, onRetry, onCreate, scopeReady } = props;
  const state = envListState({ scopeReady, isLoading, isError, vars });
  if (state === 'pick-scope') return <EmptyState compact icon="tune" title="Pick the scope" description="Choose the app, environment or platform above to list the variables defined there." />;
  if (state === 'loading') return bp === 'phone' ? <CardsSkeleton /> : <TableSkeleton />;
  if (state === 'error') return <ErrorState compact error={error} onRetry={onRetry} />;
  if (state === 'empty' || !vars) return <NoVariables onCreate={onCreate} />;
  return bp === 'phone' ? <EnvVarsCards vars={vars} now={now} actions={props} /> : <EnvVarsDesktopTable vars={vars} now={now} actions={props} />;
}

function NoVariables({ onCreate }: { onCreate: () => void }) {
  return (
    <EmptyState
      testId="env-empty"
      icon="tune"
      title="No variables at this scope"
      description="Add one, or import a .env file. Broader scopes still apply to builds — use Resolve to see the effective list."
      action={
        <Button data-testid="env-empty-add" onClick={onCreate}>
          <Icon name="add" size={18} />
          Add variable
        </Button>
      }
    />
  );
}
